/**
 * Shared access to the Wishlist King (Swish) app so theme-owned hearts
 * (cart drawer, cart page, quick view) use the same wishlist as the PDP and
 * wishlist page. The app's button component isn't loaded on every page, but
 * its core app is, via WishlistKingAppLoaderURL.
 */
(() => {
  if (window.poppyWishlist) return;

  // #region agent log
  const dbg = (hypothesisId, message, data) =>
    fetch('http://127.0.0.1:7429/ingest/b6dd23ad-08ae-4303-a1b6-768430999de0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'e1bc19' },
      body: JSON.stringify({
        sessionId: 'e1bc19',
        runId: 'wk-fix',
        hypothesisId,
        location: 'poppy-wishlist.js',
        message,
        data: { path: location.pathname, ...data },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
  // #endregion

  let appPromise;
  let items = [];
  let subscribed = false;
  const listeners = new Set();

  const getApp = () => {
    if (!appPromise) {
      appPromise = window.WishlistKing
        ? Promise.resolve(window.WishlistKing)
        : window.WishlistKingAppLoaderURL
          ? import(window.WishlistKingAppLoaderURL)
              .then(({ default: loader }) => loader.waitForCore('wishlist-app'))
              .then((core) => core?.artifact || window.WishlistKing)
          : Promise.reject(new Error('Wishlist King is not available'));
      // #region agent log
      appPromise.then(
        (app) =>
          dbg('M', 'app resolved', {
            ok: !!app?.addWishlistItem,
            mode: app?.settings?.general?.wishlistMode,
            hasSession: app?.hasSession,
          }),
        (e) => dbg('M', 'app failed', { error: String(e) })
      );
      // #endregion
    }
    return appPromise;
  };

  const isVariantMode = (app) => app?.settings?.general?.wishlistMode === 'VARIANT';

  const findItem = ({ productId, variantId }) => {
    const app = window.WishlistKing;
    const pid = Number(productId);
    const vid = Number(variantId);
    if (isVariantMode(app)) return items.find((item) => Number(item.variantId) === vid) || null;
    return (
      items.find((item) => Number(item.variantId) === vid) ||
      items.find((item) => Number(item.productId) === pid) ||
      null
    );
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    listener(items);
    if (!subscribed) {
      subscribed = true;
      getApp()
        .then((app) => {
          app.state.observeWishlist({ wishlistId: 'mine' }).subscribe((wishlist) => {
            items = Array.isArray(wishlist?.items) ? wishlist.items : [];
            // #region agent log
            dbg('N', 'wishlist update', { items: items.map((i) => ({ id: i.id, p: i.productId, v: i.variantId })) });
            // #endregion
            listeners.forEach((fn) => fn(items));
          });
        })
        .catch((error) => console.warn('[wishlist]', error));
    }
    return () => listeners.delete(listener);
  };

  const nextUpdate = () =>
    new Promise((resolve) => {
      const once = () => {
        listeners.delete(once);
        resolve();
      };
      listeners.add(once);
    });

  // The app ignores actions until its post-action refetch lands, so stay busy
  // until that refetched wishlist (with real item ids) has been delivered.
  let busy = false;
  const run = async (action) => {
    if (busy) return undefined;
    busy = true;
    try {
      const app = await getApp();
      const result = await action(app);
      if (result && subscribed) await nextUpdate();
      return result;
    } finally {
      busy = false;
    }
  };

  const add = ({ productHandle, variantId }) =>
    run(async (app) => {
      const result = await app.addWishlistItem({ productHandle, variantId: Number(variantId) || undefined });
      // #region agent log
      dbg('O', 'add result', { productHandle, variantId, id: result?.wishlistItem?.id || null });
      // #endregion
      return result;
    });

  const remove = (wishlistItemId) =>
    run(async (app) => {
      const result = await app.removeWishlistItem({ wishlistItemId });
      // #region agent log
      dbg('O', 'remove result', { wishlistItemId, ok: !!result });
      // #endregion
      return result;
    });

  window.poppyWishlist = { getApp, subscribe, findItem, add, remove, isBusy: () => busy };
})();
